import { memo, useEffect, useState } from 'react';
import t from 'react-translate';
import { useFormContext } from 'react-hook-form';
import { useAppDispatch } from 'redux/store';
import { useSelector } from 'react-redux';
import { SerializedStyles, css } from '@emotion/react';
import { Cell } from '@tanstack/react-table';

import { QuantileDistribution, AverageChangeRow, ComfortLevel, Thresholds, NpsCsatConfig, NpsCsatConfigEligibleQuestion } from 'redux/schemas/app/analytics';
import { fetchPreNpsCsatConfig } from 'redux/actions/analytics';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import { HeaderProps } from 'shared/components/nv-table';
import { NvDropdownOption } from 'shared/components/inputs/nv-dropdown';

interface ReportColumnHeaderProps<T> extends HeaderProps<T> {
  height: string | number;
  getColumnStyle: (headerIndex: number) => SerializedStyles;
}

export const ReportColumnHeader = memo(
  ({ headerGroup, className, height, getColumnStyle }: ReportColumnHeaderProps<any>) => (
    <div
      key={headerGroup.id}
      css={css`height: ${height}px;`}
      className={`${className} bg-gray-6`}
    >
      {headerGroup.headers.map(header => {
        const { header: headerTitle } = header.column.columnDef;
        const columnStyle = getColumnStyle(header.index);

        return (
          <div
            className='d-flex align-items-center label text-black pl-2'
            css={columnStyle}
            key={header.id}
          >
            {headerTitle}
          </div>
        );
      })}
    </div>
  ),
) as <T>(props: ReportColumnHeaderProps<T>) => JSX.Element;

export const QuestionCell: React.FC<{
  cell: Cell<QuantileDistribution | AverageChangeRow, string>;
  css: SerializedStyles
}> = memo(({ cell, css: cellStyles }) => (
  <div
    css={css`
      ${cellStyles}
      width: 560px;
    `}
    className='pl-2 py-3'
  >
    {cell.getValue()}
  </div>
));

/**
 * Returns tooltip text: "Rating of X" for single value, "Rating between X-Y" for range.
 * Handles edge cases where medium bucket is skipped (e.g., scale 1-2) or when thresholds are equal.
 */
export const getTooltipText = (level: ComfortLevel, thresholds: Thresholds) => {
  const [min, max] = thresholds[level];

  const ratingText = min >= max
    ? t.COURSE_ANALYTICS.PRE_POST.REPORT.QUANTILES.RATING_OF(String(min))
    : t.COURSE_ANALYTICS.PRE_POST.REPORT.QUANTILES.RATING_BETWEEN(String(max), String(min));
  return ratingText;
};

// Custom hook for fetching questions based on survey ID
export const useFetchSurveyQuestions = (
  surveyId: number | undefined,
  catalogId: string,
  filterKey: 'metricEligibleForNps' | 'metricEligibleForCsat',
) => {
  const dispatch = useAppDispatch();
  const institution = useSelector(getCurrentInstitution);
  const [questions, setQuestions] = useState<NpsCsatConfigEligibleQuestion[]>([]);

  useEffect(() => {
    if (surveyId && institution?.id && catalogId) {
      dispatch(fetchPreNpsCsatConfig({
        institutionId: institution.id,
        catalogId,
        questionSetId: +surveyId,
      }))
      .unwrap()
      .then((data: NpsCsatConfig) => {
        setQuestions(data.eligibleQuestions.filter((q) => q[filterKey]));
      })
    }
  }, [surveyId, institution?.id, catalogId, dispatch, filterKey]);

  return questions;
};

// Custom hook for syncing dropdown values from form data
export const useSyncDropdownValue = (
  fieldId: number | undefined,
  dropdownFieldName: string,
  options: NvDropdownOption[],
) => {
  const { watch, setValue } = useFormContext();
  const dropdownValue = watch(dropdownFieldName);

  useEffect(() => {
    if (fieldId && fieldId !== dropdownValue?.id) {
      setValue(dropdownFieldName, options.find((option) => option.id === fieldId) || null);
    }
  }, [fieldId, dropdownValue?.id, dropdownFieldName, options, setValue]);
};

// Utility function to transform questions to dropdown options
export const questionsToOptions = (questions: NpsCsatConfigEligibleQuestion[]): NvDropdownOption[] =>
  questions?.map((question) => ({
    type: 'text',
    id: question.id,
    value: question.id,
    text: question.questionText,
    class: 'text-wrap',
  })) || [];
